import React from 'react';
import { ChevronDown, ChevronRight, AlertCircle, XCircle } from 'lucide-react';
import { BidLineItem } from '../../models/bid';
import { VarianceBadge } from './VarianceBadge';
import {
  calculateChildrenSum,
  hasParentChildMismatch,
  calculateVariance,
  formatCurrency
} from '../../utils/lineItemHelpers';

interface LineItemCardProps {
  item: BidLineItem;
  index: number;
  level: number;
  parentIndex?: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

export const LineItemCard: React.FC<LineItemCardProps> = ({
  item,
  index,
  level,
  parentIndex,
  isExpanded,
  onToggleExpand
}) => {
  const hasChildren = item.children && item.children.length > 0;
  const childrenSum = calculateChildrenSum(item);
  const hasMismatch = hasParentChildMismatch(item);

  const itemNumber = level === 0 ? `${index + 1}` : `${parentIndex}.${index + 1}`;

  // Issues that aren't cost-related (those show as variance)
  const displayIssues = item.issues?.filter(issue => !issue.type.includes('cost')) || [];

  // For parents: sum up children's Fxer estimates (get both low and high)
  const fxerEstimateLow = hasChildren && item.children
    ? item.children.reduce((sum, child) => {
        if (child.costAnalysis?.estimate) {
          return sum + child.costAnalysis.estimate.totalLow;
        }
        return sum;
      }, 0)
    : item.costAnalysis?.estimate?.totalLow || null;

  const fxerEstimateHigh = hasChildren && item.children
    ? item.children.reduce((sum, child) => {
        if (child.costAnalysis?.estimate) {
          return sum + child.costAnalysis.estimate.totalHigh;
        }
        return sum;
      }, 0)
    : item.costAnalysis?.estimate?.totalHigh || null;

  const fxerEstimateMid = fxerEstimateLow !== null && fxerEstimateHigh !== null
    ? (fxerEstimateLow + fxerEstimateHigh) / 2
    : null;

  // Contractor's actual quote (for parents, use their stated total OR sum of children)
  const contractorTotal = item.totalCost !== null ? item.totalCost : childrenSum;

  // Only show variance if we have BOTH contractor quote AND Fxer estimate
  const showVariance = contractorTotal !== null && fxerEstimateLow !== null && fxerEstimateHigh !== null && level === 0;

  const variance = showVariance
    ? calculateVariance(contractorTotal, fxerEstimateLow!, fxerEstimateHigh!)
    : null;

  // If this is a child, render as compact row
  if (level === 1) {
    return (
      <div className="flex items-center gap-3 py-2 px-4 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
        <span className="text-xs text-gray-600 flex-shrink-0 w-8">{itemNumber}</span>
        <div className="flex-1 min-w-0 flex items-center gap-3">
          <div className="text-sm text-gray-900 leading-tight">{item.description}</div>
          {/* Fxer estimate breakdown - inline horizontal */}
          {item.costAnalysis?.estimate && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="text-gray-400">|</span>
              <span className="font-medium">Fxer Est:</span>
              <span>L: {formatCurrency(item.costAnalysis.estimate.laborCostLow || 0)}-{formatCurrency(item.costAnalysis.estimate.laborCostHigh || 0)}</span>
              <span>•</span>
              <span>M: {formatCurrency(item.costAnalysis.estimate.materialCostLow)}-{formatCurrency(item.costAnalysis.estimate.materialCostHigh)}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {displayIssues.length > 0 && (
            <div className="flex items-center gap-1">
              <XCircle className="h-3.5 w-3.5 text-red-500" />
              <span className="text-xs text-red-600 font-medium">{displayIssues.length}</span>
            </div>
          )}
          <div className="text-right w-24">
            {item.totalCost !== null ? (
              <div className="text-sm font-medium text-gray-900 leading-tight">{formatCurrency(item.totalCost)}</div>
            ) : (
              <span className="text-xs text-gray-400 italic">No quote</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Parent item - full card
  return (
    <div className="bg-white border border-gray-200 rounded-md overflow-hidden hover:shadow-sm transition-shadow">
      <div className="px-3 py-2">
        <div className="flex items-center gap-2">
          {/* Expand button */}
          {hasChildren ? (
            <button
              onClick={onToggleExpand}
              className="flex-shrink-0 hover:bg-gray-100 rounded p-0.5 -ml-0.5"
            >
              {isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
              )}
            </button>
          ) : (
            <div className="w-4 flex-shrink-0"></div>
          )}

          {/* Number & Description */}
          <div className="flex-1 min-w-0 flex items-baseline gap-2">
            <span className="text-sm font-semibold text-gray-700 flex-shrink-0">{itemNumber}.</span>
            <h3 className="text-sm text-gray-900">{item.description}</h3>
          </div>

          {/* Price section */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {fxerEstimateMid !== null && (
              <div className="text-xs text-gray-400">
                Est: {formatCurrency(fxerEstimateMid)}
              </div>
            )}
            {contractorTotal !== null ? (
              <div className="text-base font-bold text-gray-900 min-w-[90px] text-right">
                {formatCurrency(contractorTotal)}
              </div>
            ) : (
              <div className="text-sm font-medium text-gray-400 min-w-[90px] text-right">No quote</div>
            )}
            {showVariance && (
              <VarianceBadge
                contractorCost={contractorTotal!}
                marketLow={fxerEstimateLow!}
                marketHigh={fxerEstimateHigh!}
                size="sm"
                showIcon={true}
              />
            )}
          </div>
        </div>

        {/* Analysis text - compact inline */}
        {item.costAnalysis?.comparisonReason && item.costAnalysis.assessment?.toLowerCase() !== 'reasonable' && (
          <div className="mt-1.5 pl-6 text-xs text-blue-700">
            <span className="font-semibold capitalize">{item.costAnalysis.assessment}:</span> {item.costAnalysis.comparisonReason}
          </div>
        )}

        {/* Critical issues only */}
        {(hasMismatch || displayIssues.length > 0 || item.costAnalysis?.flag) && (
          <div className="mt-1.5 pl-6 space-y-0.5">
            {hasMismatch && childrenSum !== null && (
              <div className="flex items-start gap-1 text-xs text-amber-700">
                <AlertCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                <span>Sum mismatch: {formatCurrency(childrenSum)} vs {formatCurrency(item.totalCost)}</span>
              </div>
            )}
            {displayIssues.slice(0, 2).map((issue, idx) => (
              <div key={idx} className="flex items-start gap-1 text-xs text-red-700">
                <XCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                <span>{issue.description}</span>
              </div>
            ))}
            {item.costAnalysis?.flag && (
              <div className="flex items-start gap-1 text-xs text-amber-700">
                <AlertCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                <span>{item.costAnalysis.flag}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Children as compact rows */}
      {isExpanded && hasChildren && item.children && (
        <div className="border-t border-gray-200 bg-gray-50">
          <div className="px-4 py-2 flex items-center gap-3 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-100 border-b border-gray-200">
            <span className="w-8">#</span>
            <span className="flex-1">Sub-Item & Estimate Breakdown</span>
            <span className="w-24 text-right">Amount</span>
          </div>
          {item.children.map((child, childIndex) => (
            <LineItemCard
              key={child.id || `child-${childIndex}`}
              item={child}
              index={childIndex}
              level={1}
              parentIndex={index + 1}
              isExpanded={false}
              onToggleExpand={() => {}}
            />
          ))}
        </div>
      )}
    </div>
  );
};
